/**
 * Settings page — hub linking out to subscription, referrals, mailing settings,
 * accounts, etc. Designed to match the rest of the premium dark UI.
 *
 * Route: /app/settings
 */

import { motion } from "framer-motion";
import {
  Bell,
  ChevronRight,
  Crown,
  FileText,
  Gift,
  HelpCircle,
  LifeBuoy,
  MessageSquare,
  Newspaper,
  Send,
  Settings2,
  Shield,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/design";
import { useMe } from "@/api/me";
import { useSubscription } from "@/api/billing";
import { TermsSheet } from "@/components/legal/TermsSheet";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";

const SUPPORT_URL = "https://t.me/SilentPosterSupport";

export function SettingsPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: sub } = useSubscription();
  const [termsOpen, setTermsOpen] = useState(false);

  const openExternal = (url: string) => {
    haptic.select();
    if (tg) tg.openTelegramLink(url);
    else window.open(url, "_blank", "noopener");
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="pt-2">
        <div className="text-2xl font-semibold tracking-tight">Настройки</div>
        <div className="text-sm text-text-muted mt-1">
          {me?.first_name ? `Аккаунт ${me.first_name}` : "Управление приложением"}
        </div>
      </div>

      {/* Subscription card */}
      <motion.button
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => {
          haptic.impact("medium");
          navigate("/app/billing");
        }}
        className="w-full text-left"
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className={cn(
              "absolute inset-0 pointer-events-none",
              sub?.is_active
                ? "bg-gradient-to-br from-accent/25 via-transparent to-transparent"
                : "bg-gradient-to-br from-white/[0.05] via-transparent to-transparent",
            )}
            aria-hidden
          />
          <div className="relative p-5 flex items-center gap-4">
            <div className="size-12 rounded-2xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)]">
              <Crown className="size-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-text-dim uppercase tracking-wider font-medium">
                Подписка
              </div>
              <div className="text-[17px] font-semibold tracking-tight">
                {sub?.plan_title ?? "Без подписки"}
              </div>
              <div className="text-[12px] text-text-muted mt-0.5">
                {sub?.is_active && sub.days_remaining != null
                  ? `Осталось ${sub.days_remaining} ${pluralDays(sub.days_remaining)}`
                  : "Выберите план, чтобы запустить рассылку"}
              </div>
            </div>
            <ChevronRight className="size-4 text-text-dim" />
          </div>
        </Card>
      </motion.button>

      {/* Account */}
      <Section title="Аккаунт">
        <Row
          icon={<Gift className="size-4" />}
          title="Реферальная программа"
          subtitle="Получайте бесплатные дни за приглашения"
          onClick={() => navigate("/app/referrals")}
        />
        <Row
          icon={<Users className="size-4" />}
          title="Аккаунты Telegram"
          subtitle="Подключённые сессии"
          onClick={() => navigate("/app/accounts")}
        />
      </Section>

      {/* Mailing */}
      <Section title="Рассылка">
        <Row
          icon={<Settings2 className="size-4" />}
          title="Настройки рассылки"
          subtitle="Темп, тишина, спинтакс"
          onClick={() => navigate("/app/mailing")}
        />
        <Row
          icon={<MessageSquare className="size-4" />}
          title="Чаты"
          subtitle="Список и сортировка"
          onClick={() => navigate("/app/chats")}
        />
      </Section>

      {/* Misc */}
      <Section title="Помощь">
        <Row
          icon={<Send className="size-4" />}
          title="Поддержка"
          subtitle="Чат с командой SilentPoster"
          onClick={() => openExternal(SUPPORT_URL)}
        />
        <Row
          icon={<Newspaper className="size-4" />}
          title="Новости"
          subtitle="Канал с обновлениями"
          onClick={() => openExternal("https://t.me/SilentPosterNews")}
        />
        <Row
          icon={<HelpCircle className="size-4" />}
          title="Туториал"
          subtitle="Как настроить с нуля"
          onClick={() => openExternal("https://t.me/SilentPosterTutorial")}
        />
      </Section>

      {/* Admin — only for users in admin_ids */}
      {me?.is_admin && (
        <Section title="Администрирование">
          <Row
            icon={<Shield className="size-4" />}
            title="Админ-панель"
            subtitle="Юзеры, промо, рассылки"
            onClick={() => navigate("/app/admin")}
          />
        </Section>
      )}

      {/* Legal */}
      <Section title="Правовая информация">
        <Row
          icon={<FileText className="size-4" />}
          title="Условия использования"
          subtitle="Ответственность за аккаунты"
          onClick={() => setTermsOpen(true)}
        />
      </Section>

      {/* Footer / legal */}
      <div className="pt-2 pb-1 text-center text-[11px] text-text-faint flex flex-col gap-1">
        <div className="inline-flex items-center justify-center gap-1.5">
          <ShieldCheck className="size-3" />
          SilentPoster · v0.1.0
        </div>
        <div>© 2026</div>
      </div>

      {/* Reserve space — placeholder to silence unused icon warnings if added later */}
      <span className="hidden">
        <Bell />
        <LifeBuoy />
      </span>

      <TermsSheet open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
        {title}
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function Row({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.03] transition-colors active:scale-[0.998]"
    >
      <div className="size-9 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-[14px] font-medium truncate">{title}</div>
        {subtitle && (
          <div className="text-[11px] text-text-muted truncate mt-0.5">{subtitle}</div>
        )}
      </div>
      <ChevronRight className="size-4 text-text-dim shrink-0" />
    </button>
  );
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}
